/**
 * Form data collection utility
 * Single source of truth for collecting form data with modern filtering patterns
 */
/**
 * Collect form data with filtering options
 *
 * @param form The form element
 * @param options Collection options
 * @return The collected form data
 */
export const collectFormData = (form, options = {}) => {
    const { includeHidden = true, fieldFilter = null, rowSubmission = null } = options;
    // Get all form fields including web components
    const allFields = [
        ...form.querySelectorAll('input, select, textarea'),
        ...form.querySelectorAll('uo-field-input-radio, uo-field-input-switch, uo-text-field')
    ];
    // Filter fields based on options
    const filteredFields = allFields.filter((field) => {
        // Apply custom filter if provided
        if (fieldFilter && !fieldFilter(field)) {
            return false;
        }
        // Filter by row if specified
        if (rowSubmission && !rowSubmission.rowElement.contains(field)) {
            return false;
        }
        // Filter by visibility if needed
        if (!includeHidden && !isFieldVisible(field, form)) {
            return false;
        }
        return true;
    });
    // Build FormData with filtered fields
    const formData = new FormData();
    filteredFields.forEach((field) => {
        if (field.tagName.includes('-')) {
            // Handle web components
            addWebComponentData(formData, field);
        }
        else if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement) {
            // Handle standard form fields
            addStandardFieldData(formData, field);
        }
    });
    // Add row_id if this is a row submission
    if (rowSubmission && rowSubmission.rowId) {
        formData.set('row_id', rowSubmission.rowId);
    }
    return formData;
};
/**
 * Add data from web components to FormData
 * @param formData
 * @param component
 */
const addWebComponentData = (formData, component) => {
    if (component.tagName === 'UO-FIELD-INPUT-RADIO' && component.checked && component.name) {
        formData.set(component.name, component.value || '');
    }
    if (component.tagName === 'UO-FIELD-INPUT-SWITCH') {
        // Get the name from the component's name property or name attribute
        const name = component.name || component.getAttribute('name');
        if (name) {
            // Get the checked state using the most reliable method
            const checked = getSwitchCheckedState(component);
            const value = checked ? '1' : '0';
            formData.set(name, value);
        }
    }
    if (component.tagName === 'UO-TEXT-FIELD') {
        // Get the name from the component's name property or name attribute
        const name = component.name || component.getAttribute('name');
        if (name) {
            // Get the value from the component's value property
            formData.set(name, component.value || '');
        }
    }
};
/**
 * Get the checked state of a switch component using the most reliable method
 * @param component The switch component
 * @return boolean indicating if the switch is checked
 */
const getSwitchCheckedState = (component) => {
    // Method 1: Direct property access (most reliable for well-implemented components)
    if (typeof component.checked === 'boolean') {
        return component.checked;
    }
    // Method 2: Check the internal input element in shadow DOM
    if (component.shadowRoot) {
        const input = component.shadowRoot.querySelector('input[type="checkbox"]');
        if (input && typeof input.checked === 'boolean') {
            return input.checked;
        }
    }
    // Method 3: Check for checked attribute (fallback)
    return component.hasAttribute('checked');
};
/**
 * Add data from standard form fields to FormData
 * @param formData
 * @param field
 */
const addStandardFieldData = (formData, field) => {
    // Type guard for input fields that can be checked
    if (field instanceof HTMLInputElement) {
        if (field.type === 'radio') {
            if (field.checked) {
                formData.set(field.name, field.value);
            }
        }
        else if (field.type === 'checkbox') {
            if (field.checked) {
                formData.set(field.name, field.value || 'on');
            }
        }
        else {
            formData.set(field.name, field.value);
        }
    }
    else {
        // Handle select and textarea elements
        formData.set(field.name, field.value);
    }
};
/**
 * Check if a field is visible (not hidden by section toggle)
 * @param field
 * @param form
 */
const isFieldVisible = (field, form) => {
    // Check if the field itself is hidden
    if (field instanceof HTMLElement && (field.style.display === 'none' || field.hidden)) {
        return false;
    }
    // Check if parent section is hidden
    let parent = field.parentElement;
    while (parent && parent !== form) {
        // Check for hidden section components
        if (parent.tagName === 'UAP-APP-INTEGRATION-SETTINGS-SECTION') {
            const section = parent;
            if (section.visible === false) {
                return false;
            }
        }
        // Also check for CSS hidden sections
        if (parent.classList.contains('section') && parent instanceof HTMLElement && parent.style.display === 'none') {
            return false;
        }
        parent = parent.parentElement;
    }
    return true;
};
/**
 * Get only visible required fields for validation
 *
 * NOTE: This function is complex because we need to:
 * 1. Find all required fields in the form
 * 2. Filter out fields that are hidden by dynamic sections
 * 3. Handle both native HTML elements and web components
 * 4. Ensure web components are included for validation even if they don't have native validation methods
 *
 * TODO: This should be simplified when moving to a proper form state management system
 * @param form
 */
export const getVisibleRequiredFields = (form) => {
    const requiredFields = form.querySelectorAll('[required]');
    const visibleFields = Array.from(requiredFields).filter((field) => {
        // Use the same visibility logic as isFieldVisible
        if (!isFieldVisible(field, form)) {
            return false;
        }
        // For web components, check if they have validation methods or are valid
        if (field.tagName.includes('-')) {
            // Web components - check if they have validation or are valid
            const webComponent = field;
            if (webComponent.checkValidity && typeof webComponent.checkValidity === 'function') {
                return true; // Has native validation
            }
            // For web components without native validation, check if they have value
            if (webComponent.required && (!webComponent.value || webComponent.value.trim() === '')) {
                return true; // Include for validation (will be handled by custom logic)
            }
            return false;
        }
        // For native form elements, ensure they have checkValidity method
        return !!field.checkValidity && typeof field.checkValidity === 'function';
    });
    return visibleFields;
};
